import { useFadeIn } from "@/hooks/useFadeIn";
import areasWeServeMassachusetts from "@/assets/areas-we-serve-massachusetts.jpg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const serviceAreas = [
  {
    title: "Greater Boston",
    locations:
      "Boston · Cambridge · Somerville · Chelsea · Revere · Everett · Brookline · Newton",
  },
  {
    title: "North Shore",
    locations:
      "Lynn · Swampscott · Marblehead · Salem · Peabody · Beverly · Gloucester · Danvers · Newburyport",
  },
  {
    title: "MetroWest",
    locations: "Framingham · Waltham · Watertown · Marlborough · Natick",
  },
  {
    title: "South Shore",
    locations: "Quincy · Braintree · Weymouth · Hingham",
  },
  {
    title: "Cape Cod",
    locations: "Barnstable · Falmouth · Yarmouth · Dennis",
  },
];

const AreasWeServeSection = () => {
  const { ref, visible } = useFadeIn();

  return (
    <section className="border-t border-neutral-200 bg-[#f7f5f0] py-12 md:py-16">
      <div
        ref={ref}
        className={`mx-auto max-w-wide px-5 md:px-12 transition-all duration-700 ${
          visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
        }`}
      >
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(340px,0.95fr)] lg:gap-14 xl:gap-16">
          <div className="max-w-2xl text-left lg:pr-8 xl:pr-12">
            <div className="max-w-xl text-left">
              <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                Local Expertise
              </p>
              <h2 className="mt-3 font-headline text-[2.1rem] text-neutral-950 md:text-[2.9rem] md:leading-[1.08]">
                Areas We Serve Across Massachusetts
              </h2>
              <p className="mt-4 font-headline text-xl text-neutral-900 md:text-[1.65rem]">
                Expert guidance across city living, coastal communities, and key
                growth markets
              </p>
              <p className="mt-5 text-sm leading-[1.7] text-neutral-700 md:text-base">
                From city living to coastal communities, Hadassah Homes provides
                expert real estate guidance across Massachusetts.
              </p>
              <p className="mt-3 text-sm leading-[1.7] text-neutral-600 md:text-base">
                Whether you&apos;re buying, selling, or investing, we bring a
                strategy-first approach tailored to each local market.
              </p>
            </div>

            <div className="mt-10 text-left md:mt-12">
              <Accordion type="multiple" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {serviceAreas.map((area, index) => (
                  <AccordionItem
                    key={area.title}
                    value={`area-${index}`}
                    className="border border-neutral-200 bg-white/70 px-5 md:px-6"
                  >
                    <AccordionTrigger className="py-5 text-left font-headline text-xl text-neutral-950 transition-colors hover:text-accent hover:no-underline md:text-2xl">
                      {area.title}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-left">
                      <div className="flex flex-wrap gap-x-2 gap-y-2.5 text-left text-sm leading-relaxed md:text-[15px]">
                        {area.locations
                          .split(" · ")
                          .map((city, cityIndex, cities) => (
                            <span
                              key={`${area.title}-${city}`}
                              className="inline-flex items-center"
                            >
                              <span
                                className="text-neutral-600"
                              >
                                {city}
                              </span>
                              {cityIndex < cities.length - 1 ? (
                                <span className="ml-2 text-neutral-300">·</span>
                              ) : null}
                            </span>
                          ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          <div className="mt-8 flex items-center lg:mt-0">
            <div className="relative h-[340px] w-full overflow-hidden border border-neutral-200 shadow-sm md:h-[380px] lg:h-[520px]">
              <img
                src={areasWeServeMassachusetts}
                alt="Diverse communities and neighborhoods across Massachusetts"
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
                width={1400}
                height={1050}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/28 via-black/8 to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AreasWeServeSection;
